/**
 * Relatório de Comissões — visão consolidada por vendedor + período.
 *
 * Filtros:
 *   • Vendedor (obrigatório para métricas por-vendedor; "Todos" agrega geral)
 *   • Data: Hoje / Esta semana / Este mês / Período específico (start+end)
 *   • Última seleção fica salva no localStorage (mesma UX do Histórico).
 *
 * Métricas: Total vendido, # vendas, comissão pendente, paga, total, e lista
 * detalhada com botão para abrir o recibo da venda vinculada.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, brl } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Calendar as CalendarIcon, DollarSign, TrendingUp, ClipboardList, Check, RotateCcw, CheckCheck } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import Receipt from "@/components/Receipt";
import { Money, HideMoneyButton } from "@/contexts/HiddenValues";
import { toast } from "sonner";

const DATE_PRESET_KEY = "gestaoexpert:comissoes:date_preset";
const DATE_SPECIFIC_KEY = "gestaoexpert:comissoes:date_specific";
const DATE_START_KEY = "gestaoexpert:comissoes:date_start";
const DATE_END_KEY = "gestaoexpert:comissoes:date_end";
const SELLER_KEY = "gestaoexpert:comissoes:seller";

function computeRange(preset, specific, start, end) {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  if (preset === "all") return { s: null, e: null };
  if (preset === "today") return { s: startOfDay(now), e: endOfDay(now) };
  if (preset === "week") {
    const monday = new Date(now);
    const day = (monday.getDay() + 6) % 7;
    monday.setDate(monday.getDate() - day);
    return { s: startOfDay(monday), e: endOfDay(now) };
  }
  if (preset === "month") return { s: new Date(now.getFullYear(), now.getMonth(), 1), e: endOfDay(now) };
  if (preset === "specific" && specific) {
    const d = new Date(`${specific}T00:00:00`);
    return { s: startOfDay(d), e: endOfDay(d) };
  }
  if (preset === "range" && start && end) {
    return { s: new Date(`${start}T00:00:00`), e: new Date(`${end}T23:59:59`) };
  }
  return { s: null, e: null };
}

export default function Comissoes() {
  const [sellers, setSellers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState(() => {
    try { return localStorage.getItem(SELLER_KEY) || "all"; } catch { return "all"; }
  });
  const [preset, setPreset] = useState(() => {
    try { return localStorage.getItem(DATE_PRESET_KEY) || "month"; } catch { return "month"; }
  });
  const [specific, setSpecific] = useState(() => {
    try { return localStorage.getItem(DATE_SPECIFIC_KEY) || new Date().toISOString().slice(0, 10); } catch { return new Date().toISOString().slice(0, 10); }
  });
  const [rangeStart, setRangeStart] = useState(() => {
    try { return localStorage.getItem(DATE_START_KEY) || ""; } catch { return ""; }
  });
  const [rangeEnd, setRangeEnd] = useState(() => {
    try { return localStorage.getItem(DATE_END_KEY) || ""; } catch { return ""; }
  });
  const [data, setData] = useState({ total_sold: 0, sales_count: 0, total_pending: 0, total_paid: 0, total: 0, commissions: [] });
  const [store, setStore] = useState({});
  const [showOrder, setShowOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "pending" | "paid"
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [revertTarget, setRevertTarget] = useState(null); // row alvo para reverter para pendente

  useEffect(() => { try { localStorage.setItem(SELLER_KEY, selectedSeller); } catch { /* noop */ } }, [selectedSeller]);
  useEffect(() => { try { localStorage.setItem(DATE_PRESET_KEY, preset); } catch { /* noop */ } }, [preset]);
  useEffect(() => { try { localStorage.setItem(DATE_SPECIFIC_KEY, specific); } catch { /* noop */ } }, [specific]);
  useEffect(() => { try { localStorage.setItem(DATE_START_KEY, rangeStart); } catch { /* noop */ } }, [rangeStart]);
  useEffect(() => { try { localStorage.setItem(DATE_END_KEY, rangeEnd); } catch { /* noop */ } }, [rangeEnd]);

  useEffect(() => {
    api.get("/sellers").then(r => setSellers(r.data || [])).catch(() => {});
    api.get("/store").then(r => setStore(r.data || {})).catch(() => {});
  }, []);

  const range = useMemo(() => computeRange(preset, specific, rangeStart, rangeEnd), [preset, specific, rangeStart, rangeEnd]);

  const loadReport = async () => {
    setBusy(true);
    try {
      const params = {};
      if (selectedSeller !== "all") params.seller_id = selectedSeller;
      if (range.s && range.e) {
        params.start = range.s.toISOString();
        params.end = range.e.toISOString();
      }
      const r = await api.get("/commissions/report", { params });
      setData(r.data);
    } catch (e) {
      toast.error("Erro ao carregar relatório");
    } finally { setBusy(false); }
  };
  useEffect(() => { loadReport(); /* eslint-disable-next-line */ }, [selectedSeller, preset, specific, rangeStart, rangeEnd]);

  const openReceipt = async (oid) => {
    try {
      const r = await api.get(`/orders/${oid}`);
      setShowOrder(r.data);
    } catch { /* ignore */ }
  };

  const toggleCommissionStatus = async (row, nextStatusOverride) => {
    const nextStatus = nextStatusOverride || (row.commission_status === "paid" ? "pending" : "paid");
    setTogglingId(row.order_id);
    try {
      await api.put(`/orders/${row.order_id}/commission`, {
        amount: row.commission_amount,
        status: nextStatus,
      });
      toast.success(nextStatus === "paid" ? "Comissão marcada como paga" : "Comissão voltou para pendente");
      await loadReport();
    } catch {
      toast.error("Erro ao atualizar comissão");
    } finally { setTogglingId(null); setRevertTarget(null); }
  };

  const markAllPaid = async () => {
    setMarkingAll(true);
    try {
      const params = {};
      if (selectedSeller !== "all") params.seller_id = selectedSeller;
      if (range.s && range.e) {
        params.start = range.s.toISOString();
        params.end = range.e.toISOString();
      }
      const r = await api.post("/commissions/mark-all-paid", null, { params });
      const n = r.data?.updated || 0;
      if (n > 0) toast.success(`${n} ${n === 1 ? "comissão marcada" : "comissões marcadas"} como paga${n === 1 ? "" : "s"}`);
      else toast.info("Nenhuma comissão pendente no filtro atual");
      setConfirmAllOpen(false);
      await loadReport();
    } catch {
      toast.error("Erro ao marcar comissões");
    } finally { setMarkingAll(false); }
  };

  const filteredCommissions = useMemo(() => {
    if (statusFilter === "all") return data.commissions;
    return data.commissions.filter(c => (c.commission_status || "pending") === statusFilter);
  }, [data.commissions, statusFilter]);
  const hasAnyPending = useMemo(() => data.commissions.some(c => (c.commission_status || "pending") === "pending"), [data.commissions]);

  return (
    <div className="space-y-6" data-testid="comissoes-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="label-tiny mb-2">Relatório</p>
          <h1 className="font-display text-4xl tracking-tight">Comissões</h1>
          <p className="text-sm text-neutral-500 mt-1">Acompanhe o volume de vendas e comissões de cada vendedor.</p>
        </div>
        <HideMoneyButton testId="comissoes-hide-money"/>
      </div>

      {/* Filtros */}
      <Card className="p-4 border-[#E5E5E5]">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Vendedor</label>
            <Select value={selectedSeller} onValueChange={setSelectedSeller}>
              <SelectTrigger data-testid="comissoes-seller-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {sellers.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Período</label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger data-testid="comissoes-date-preset">
                <CalendarIcon size={13} className="mr-1.5 opacity-60"/>
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as datas</SelectItem>
                <SelectItem value="today">Hoje</SelectItem>
                <SelectItem value="week">Esta semana</SelectItem>
                <SelectItem value="month">Este mês</SelectItem>
                <SelectItem value="specific">Data específica</SelectItem>
                <SelectItem value="range">Período específico</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {preset === "specific" && (
            <div>
              <label className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Data</label>
              <Input type="date" value={specific} onChange={e=>setSpecific(e.target.value)} data-testid="comissoes-date-specific"/>
            </div>
          )}
          {preset === "range" && (
            <>
              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">De</label>
                <Input type="date" value={rangeStart} onChange={e=>setRangeStart(e.target.value)} data-testid="comissoes-date-start"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">Até</label>
                <Input type="date" value={rangeEnd} onChange={e=>setRangeEnd(e.target.value)} data-testid="comissoes-date-end"/>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-4 border-[#E5E5E5]">
          <div className="text-xs uppercase text-neutral-500 flex items-center gap-1.5"><TrendingUp size={12}/>Total vendido</div>
          <div className="font-display text-2xl mt-1" data-testid="metric-total-sold"><Money value={data.total_sold}/></div>
        </Card>
        <Card className="p-4 border-[#E5E5E5]">
          <div className="text-xs uppercase text-neutral-500 flex items-center gap-1.5"><ClipboardList size={12}/>Qtd. vendas</div>
          <div className="font-display text-2xl mt-1" data-testid="metric-sales-count">{data.sales_count || 0}</div>
        </Card>
        <button
          type="button"
          onClick={()=>setStatusFilter(statusFilter === "pending" ? "all" : "pending")}
          className={`text-left rounded-lg transition ${statusFilter === "pending" ? "ring-2 ring-amber-500 ring-offset-1 dark:ring-offset-neutral-900" : "hover:brightness-95"}`}
          data-testid="metric-pending-btn"
          title="Clique para ver apenas comissões pendentes"
        >
          <Card className="p-4 border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 h-full">
            <div className="text-xs uppercase text-amber-700 dark:text-amber-300 flex items-center justify-between">
              <span>Pendente</span>
              {statusFilter === "pending" && <span className="text-[10px] font-semibold">FILTRADO</span>}
            </div>
            <div className="font-display text-2xl mt-1 text-amber-700 dark:text-amber-200" data-testid="metric-pending"><Money value={data.total_pending}/></div>
          </Card>
        </button>
        <button
          type="button"
          onClick={()=>setStatusFilter(statusFilter === "paid" ? "all" : "paid")}
          className={`text-left rounded-lg transition ${statusFilter === "paid" ? "ring-2 ring-emerald-500 ring-offset-1 dark:ring-offset-neutral-900" : "hover:brightness-95"}`}
          data-testid="metric-paid-btn"
          title="Clique para ver apenas comissões pagas"
        >
          <Card className="p-4 border-emerald-200 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/30 h-full">
            <div className="text-xs uppercase text-emerald-700 dark:text-emerald-300 flex items-center justify-between">
              <span>Pago</span>
              {statusFilter === "paid" && <span className="text-[10px] font-semibold">FILTRADO</span>}
            </div>
            <div className="font-display text-2xl mt-1 text-emerald-700 dark:text-emerald-200" data-testid="metric-paid"><Money value={data.total_paid}/></div>
          </Card>
        </button>
        <Card className="p-4 border-[#E5E5E5]">
          <div className="text-xs uppercase text-neutral-500 flex items-center gap-1.5"><DollarSign size={12}/>Total comissão</div>
          <div className="font-display text-2xl mt-1" data-testid="metric-total"><Money value={data.total}/></div>
        </Card>
      </div>

      {/* Lista de comissões */}
      <Card className="p-0 border-[#E5E5E5] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium">
              Comissões no período
              {statusFilter !== "all" && (
                <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-white/10 text-neutral-700 dark:text-neutral-200">
                  {statusFilter === "pending" ? "só pendentes" : "só pagas"}
                </span>
              )}
            </div>
            {statusFilter !== "all" && (
              <Button size="sm" variant="ghost" onClick={()=>setStatusFilter("all")} className="h-7 text-xs" data-testid="clear-status-filter">
                Limpar filtro
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {busy && <div className="text-xs text-neutral-500">carregando…</div>}
            {hasAnyPending && (
              <Button
                size="sm"
                onClick={()=>setConfirmAllOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="mark-all-paid-btn"
              >
                <CheckCheck size={14} className="mr-1.5"/> Marcar todas como paga
              </Button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-white/5 border-b border-[#E5E5E5] text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Nº</th>
                <th className="px-4 py-3 font-medium">Data</th>
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Produtos</th>
                <th className="px-4 py-3 font-medium">Vendedor</th>
                <th className="px-4 py-3 font-medium text-right">Venda</th>
                <th className="px-4 py-3 font-medium text-right">Comissão</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filteredCommissions.map(c => (
                <tr key={c.order_id} className="border-b border-[#E5E5E5] hover:bg-neutral-50 dark:hover:bg-white/5" data-testid={`comission-row-${c.order_id}`}>
                  <td className="px-4 py-3 font-mono text-xs">#{String(c.order_number || "").padStart(6,"0")}</td>
                  <td className="px-4 py-3 text-xs">{c.sale_date ? new Date(c.sale_date).toLocaleDateString('pt-BR') : "—"}</td>
                  <td className="px-4 py-3 text-xs" data-testid={`comission-customer-${c.order_id}`}>{c.customer_name || "—"}</td>
                  <td className="px-4 py-3 text-xs text-neutral-700 dark:text-neutral-300 max-w-[240px] truncate" title={(c.items||[]).map(it=>`${it.quantity>1?it.quantity+'× ':''}${it.name}`).join(", ")}>
                    {(c.items||[]).slice(0,2).map(it => it.quantity>1?`${it.quantity}× ${it.name}`:it.name).join(", ")}
                    {(c.items||[]).length > 2 && ` +${c.items.length-2}`}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{c.created_by}</td>
                  <td className="px-4 py-3 text-right"><Money value={c.total}/></td>
                  <td className="px-4 py-3 text-right font-medium"><Money value={c.commission_amount}/></td>
                  <td className="px-4 py-3">
                    {c.commission_status === "paid"
                      ? <span className="inline-block px-2 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200">Pago</span>
                      : <span className="inline-block px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">Pendente</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1.5">
                      {c.commission_status === "paid" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={()=>setRevertTarget(c)}
                          disabled={togglingId === c.order_id}
                          data-testid={`comission-mark-pending-${c.order_id}`}
                          title="Reverter para pendente"
                        >
                          <RotateCcw size={12} className="mr-1"/> Pendente
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={()=>toggleCommissionStatus(c)}
                          disabled={togglingId === c.order_id}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`comission-mark-paid-${c.order_id}`}
                          title="Marcar como paga"
                        >
                          <Check size={12} className="mr-1"/> Pagar
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={()=>openReceipt(c.order_id)} data-testid={`comission-open-${c.order_id}`}>
                        <Eye size={12} className="mr-1"/> Ver
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredCommissions.length === 0 && (
                <tr><td colSpan="9" className="text-center py-12 text-neutral-500 text-sm">
                  {statusFilter === "pending" ? "Nenhuma comissão pendente nos filtros aplicados." :
                   statusFilter === "paid" ? "Nenhuma comissão paga nos filtros aplicados." :
                   "Nenhuma comissão no período/vendedor selecionado."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Confirmação: reverter comissão paga → pendente */}
      <AlertDialog open={!!revertTarget} onOpenChange={(o)=>{ if (!o) setRevertTarget(null); }}>
        <AlertDialogContent data-testid="confirm-revert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reverter esta comissão para pendente?</AlertDialogTitle>
            <AlertDialogDescription>
              Você vai reverter uma comissão <strong>já marcada como paga</strong>
              {revertTarget?.order_number ? <> do pedido <strong>#{String(revertTarget.order_number).padStart(6,"0")}</strong></> : null}
              {revertTarget?.created_by ? <> ({revertTarget.created_by})</> : null}
              {" "}no valor de <strong>{brl(revertTarget?.commission_amount || 0)}</strong>.
              <br/>Ao reverter, a comissão volta a contar como <strong>pendente</strong> e <strong>deixa de descontar</strong> do lucro líquido no Financeiro.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={togglingId === revertTarget?.order_id}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e)=>{ e.preventDefault(); if (revertTarget) toggleCommissionStatus(revertTarget, "pending"); }}
              disabled={togglingId === revertTarget?.order_id}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-revert-yes"
            >
              {togglingId === revertTarget?.order_id ? "Revertendo…" : "Sim, reverter"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmação: marcar todas como paga */}
      <AlertDialog open={confirmAllOpen} onOpenChange={setConfirmAllOpen}>
        <AlertDialogContent data-testid="confirm-mark-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar todas as comissões pendentes como pagas?</AlertDialogTitle>
            <AlertDialogDescription>
              Todas as comissões <strong>pendentes</strong> que casam com os filtros atuais (vendedor + período) serão marcadas como pagas com a data de hoje.
              <br/>Elas passarão a descontar do lucro líquido no Financeiro.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={markingAll}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e)=>{ e.preventDefault(); markAllPaid(); }}
              disabled={markingAll}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-mark-all-yes"
            >
              {markingAll ? "Marcando…" : "Sim, marcar todas"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showOrder && <Receipt sale={showOrder} store={store} wideLayout onClose={()=>setShowOrder(null)}/>}
    </div>
  );
}
